/**
 * Supabase API for Click&Share v2 Return App
 *
 * Provides functions for:
 * - Fetching bookings and items
 * - Loading merchant configurations
 * - Submitting return reports
 * - Uploading photos
 * - Managing return versions
 */

import { createClient } from '@supabase/supabase-js';
import { hashBookingToken } from './tokenUtils';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseKey);

// ============================================================================
// NETWORK RETRY HELPERS
// ============================================================================

/**
 * Detect transient fetch-layer failures. These happen on mobile Safari when
 * the network handoff (cellular/Wi-Fi) or tab backgrounding aborts the
 * in-flight request — they surface as `TypeError: Load failed` (WebKit) or
 * `TypeError: Failed to fetch` (Chromium/Firefox). Supabase API errors carry
 * a `.code` and must NOT be retried here.
 */
export const isTransientNetworkError = (error) => {
  if (!error) return false;
  if (error.code) return false;
  if (error.name === 'TypeError') return true;
  const msg = typeof error.message === 'string' ? error.message : '';
  return /load failed|failed to fetch|networkerror|network request failed/i.test(msg);
};

/**
 * Retry an async operation on transient network errors with exponential backoff.
 */
const withNetworkRetry = async (fn, { retries = 2, baseDelayMs = 300 } = {}) => {
  let attempt = 0;
  while (true) {
    try {
      return await fn();
    } catch (error) {
      if (attempt >= retries || !isTransientNetworkError(error)) {
        throw error;
      }
      const delay = baseDelayMs * Math.pow(3, attempt);
      console.warn(`[supabase] Transient network error (attempt ${attempt + 1}/${retries + 1}), retrying in ${delay}ms`);
      await new Promise(resolve => setTimeout(resolve, delay));
      attempt++;
    }
  }
};

// ============================================================================
// TOKEN RESOLUTION
// ============================================================================

/**
 * Resolve a raw booking token to a validated booking context.
 *
 * Flow:
 * 1. Hash the raw token with SHA-256
 * 2. Call RPC `resolve_booking_token` which validates the token and returns
 *    the booking with merchant/location/language context in one round-trip
 * 3. Fallback: query booking_tokens directly if RPC doesn't exist
 *
 * @param {string} rawToken - The raw token from the /b/:token route
 * @returns {Promise<{booking_id: string, status: string}>} Resolved booking_id
 * @throws {Error} With descriptive code: TOKEN_INVALID, TOKEN_EXPIRED, TOKEN_REVOKED
 */
export const resolveBookingToken = (rawToken) =>
  withNetworkRetry(() => resolveBookingTokenOnce(rawToken));

const resolveBookingTokenOnce = async (rawToken) => {
  try {
    const tokenHash = await hashBookingToken(rawToken);

    // Try RPC function first (preferred — keeps validation server-side)
    const { data: rpcData, error: rpcError } = await supabase
      .rpc('resolve_booking_token', { p_token_hash: tokenHash });

    // If RPC function doesn't exist (PGRST202), fall back to direct query
    if (rpcError && rpcError.code === 'PGRST202') {
      console.log('RPC resolve_booking_token not found, falling back to direct query');

      const { data, error } = await supabase
        .from('booking_tokens')
        .select('booking_id, expires_at, revoked_at')
        .eq('token_hash', tokenHash)
        .maybeSingle();

      if (error) {
        console.error('Error querying booking_tokens:', error);
        throw error;
      }

      if (!data) {
        const err = new Error('Invalid token');
        err.code = 'TOKEN_INVALID';
        throw err;
      }

      if (data.revoked_at) {
        const err = new Error('Token has been revoked');
        err.code = 'TOKEN_REVOKED';
        throw err;
      }

      if (new Date(data.expires_at) <= new Date()) {
        const err = new Error('Token has expired');
        err.code = 'TOKEN_EXPIRED';
        throw err;
      }

      return { booking_id: data.booking_id, status: 'valid' };
    }

    if (rpcError) {
      console.error('Error resolving booking token:', rpcError);
      throw rpcError;
    }

    // RPC returns an array with a single row containing status + booking_id
    const result = rpcData && rpcData.length > 0 ? rpcData[0] : null;

    if (!result) {
      const err = new Error('Invalid token');
      err.code = 'TOKEN_INVALID';
      throw err;
    }

    // RPC returns { status, booking_id } where status can be 'valid', 'expired', 'revoked', 'not_found'
    if (result.status === 'not_found') {
      const err = new Error('Invalid token');
      err.code = 'TOKEN_INVALID';
      throw err;
    }

    if (result.status === 'revoked') {
      const err = new Error('Token has been revoked');
      err.code = 'TOKEN_REVOKED';
      throw err;
    }

    if (result.status === 'expired') {
      const err = new Error('Token has expired');
      err.code = 'TOKEN_EXPIRED';
      throw err;
    }

    return { booking_id: result.booking_id, status: 'valid' };
  } catch (error) {
    // Re-throw token-specific errors as-is
    if (error.code === 'TOKEN_INVALID' || error.code === 'TOKEN_EXPIRED' || error.code === 'TOKEN_REVOKED') {
      throw error;
    }
    console.error('Unexpected error in resolveBookingToken:', error);
    throw error;
  }
};

// ============================================================================
// BOOKING OPERATIONS
// ============================================================================

/**
 * Fetch booking by booking ID
 * Uses RPC function to bypass RLS on bookings table (with fallback to direct query)
 */
export const fetchBookingById = (bookingId) =>
  withNetworkRetry(() => fetchBookingByIdOnce(bookingId));

const fetchBookingByIdOnce = async (bookingId) => {
  try {
    console.log('Fetching booking with id:', bookingId);

    // Try RPC function first (works with RLS enabled)
    const { data: rpcData, error: rpcError } = await supabase
      .rpc('get_booking_by_id', { p_booking_id: bookingId });

    // If RPC function doesn't exist (PGRST202), fall back to direct query
    if (rpcError && rpcError.code === 'PGRST202') {
      console.log('RPC function not found, falling back to direct query');
      const { data, error } = await supabase
        .from('bookings')
        .select('*')
        .eq('id', bookingId)
        .single();

      if (error) {
        console.error('Error fetching booking:', error);
        throw error;
      }

      console.log('Booking found:', data);
      return data;
    }

    if (rpcError) {
      console.error('Error fetching booking:', rpcError);
      throw rpcError;
    }

    // RPC returns an array, get the first result
    const booking = rpcData && rpcData.length > 0 ? rpcData[0] : null;

    if (!booking) {
      throw { code: 'PGRST116', message: 'No booking found with this id' };
    }

    console.log('Booking found:', booking);
    return booking;
  } catch (error) {
    console.error('Unexpected error in fetchBookingById:', error);
    throw error;
  }
};

/**
 * Fetch booking items for a booking
 * Uses RPC function to bypass RLS on booking_items table (with fallback to direct query)
 */
export const fetchBookingItems = (bookingId) =>
  withNetworkRetry(() => fetchBookingItemsOnce(bookingId));

const fetchBookingItemsOnce = async (bookingId) => {
  try {
    console.log('Fetching booking items for booking:', bookingId);

    // Try RPC function first (works with RLS enabled)
    const { data: rpcData, error: rpcError } = await supabase
      .rpc('get_booking_items', { p_booking_id: bookingId });

    // If RPC function doesn't exist (PGRST202), fall back to direct query
    if (rpcError && rpcError.code === 'PGRST202') {
      console.log('RPC function not found, falling back to direct query');
      const { data, error } = await supabase
        .from('booking_items')
        .select('*')
        .eq('booking_id', bookingId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching booking items:', error);
        throw error;
      }

      console.log('Booking items found:', data?.length || 0);
      return data || [];
    }

    if (rpcError) {
      console.error('Error fetching booking items:', rpcError);
      throw rpcError;
    }

    console.log('Booking items found:', rpcData?.length || 0);
    return rpcData || [];
  } catch (error) {
    console.error('Unexpected error in fetchBookingItems:', error);
    throw error;
  }
};

/**
 * Fetch inventory items for booking items
 */
export const fetchInventoryItemsForBooking = (bookingItems) =>
  withNetworkRetry(() => fetchInventoryItemsForBookingOnce(bookingItems));

const fetchInventoryItemsForBookingOnce = async (bookingItems) => {
  try {
    if (!bookingItems || bookingItems.length === 0) {
      return [];
    }

    // Get all inventory_item_ids (filter out nulls)
    const inventoryItemIds = bookingItems
      .map(item => item.inventory_item_id)
      .filter(id => id !== null);

    if (inventoryItemIds.length === 0) {
      return [];
    }

    console.log('Fetching inventory items:', inventoryItemIds.length);

    // Use RPC function (works with RLS enabled — anon's direct column grant on
    // inventory_items was narrowed to (size, product_type_id, active) by the
    // RLS-hardening migration, so `.select('*')` is no longer permitted).
    // Falls back to a direct query of the still-permitted display columns if
    // the RPC is not deployed.
    const { data: rpcData, error: rpcError } = await supabase
      .rpc('get_inventory_items_for_return', { p_ids: inventoryItemIds });

    if (rpcError && rpcError.code === 'PGRST202') {
      console.log('RPC get_inventory_items_for_return not found, falling back to direct query');
      const { data, error } = await supabase
        .from('inventory_items')
        .select('id, size')
        .in('id', inventoryItemIds);

      if (error) {
        console.error('Error fetching inventory items:', error);
        throw error;
      }

      console.log('Inventory items found:', data?.length || 0);
      return data || [];
    }

    if (rpcError) {
      console.error('Error fetching inventory items:', rpcError);
      throw rpcError;
    }

    console.log('Inventory items found:', rpcData?.length || 0);
    return rpcData || [];
  } catch (error) {
    console.error('Unexpected error in fetchInventoryItemsForBooking:', error);
    throw error;
  }
};

/**
 * Fetch product types by id list (returns id + inventory_type).
 * Used to identify pool/unlimited items so they can be excluded from
 * per-item return flows.
 */
export const fetchProductTypesByIds = (productTypeIds) =>
  withNetworkRetry(() => fetchProductTypesByIdsOnce(productTypeIds));

const fetchProductTypesByIdsOnce = async (productTypeIds) => {
  try {
    const uniqueIds = [...new Set((productTypeIds || []).filter(Boolean))];
    if (uniqueIds.length === 0) {
      return [];
    }

    const { data, error } = await supabase
      .from('product_types')
      .select('id, inventory_type')
      .in('id', uniqueIds);

    if (error) {
      console.error('Error fetching product types:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Unexpected error in fetchProductTypesByIds:', error);
    throw error;
  }
};

// ============================================================================
// CONFIGURATION OPERATIONS
// ============================================================================

/**
 * Fetch merchant data (for colors, name, etc.)
 */
export const fetchMerchant = (merchantId) =>
  withNetworkRetry(() => fetchMerchantOnce(merchantId));

const fetchMerchantOnce = async (merchantId) => {
  try {
    console.log('Fetching merchant:', merchantId);

    // Use RPC function (works with RLS enabled — anon has no direct read on
    // merchants after the RLS-hardening migration). Falls back to a direct
    // query if the RPC is not deployed.
    const { data: rpcData, error: rpcError } = await supabase
      .rpc('get_merchant_public', { p_merchant_id: merchantId });

    if (rpcError && rpcError.code === 'PGRST202') {
      console.log('RPC get_merchant_public not found, falling back to direct query');
      const { data, error } = await supabase
        .from('merchants')
        .select('id, merchant_id, business_name, display_name, color1, color2')
        .eq('id', merchantId)
        .single();

      if (error) {
        console.error('Error fetching merchant:', error);
        throw error;
      }

      console.log('Merchant found:', data?.business_name);
      return data;
    }

    if (rpcError) {
      console.error('Error fetching merchant:', rpcError);
      throw rpcError;
    }

    const merchant = rpcData && rpcData.length > 0 ? rpcData[0] : null;
    if (!merchant) {
      throw { code: 'PGRST116', message: 'No merchant found with this id' };
    }

    console.log('Merchant found:', merchant.business_name);
    return merchant;
  } catch (error) {
    console.error('Unexpected error in fetchMerchant:', error);
    throw error;
  }
};

/**
 * Fetch merchant configuration
 */
export const fetchMerchantConfig = (merchantId) =>
  withNetworkRetry(() => fetchMerchantConfigOnce(merchantId));

const fetchMerchantConfigOnce = async (merchantId) => {
  try {
    console.log('Fetching merchant config for:', merchantId);

    // Use RPC function (works with RLS enabled — anon has no direct read on
    // merchant_configurations after the RLS-hardening migration). Falls back
    // to a direct query if the RPC is not deployed.
    const { data: rpcData, error: rpcError } = await supabase
      .rpc('get_merchant_config', { p_merchant_id: merchantId });

    if (rpcError && rpcError.code === 'PGRST202') {
      console.log('RPC get_merchant_config not found, falling back to direct query');
      const { data, error } = await supabase
        .from('merchant_configurations')
        .select('*')
        .eq('merchant_id', merchantId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No configuration found, return null
          console.warn('No merchant configuration found for:', merchantId);
          return null;
        }
        console.error('Error fetching merchant config:', error);
        throw error;
      }

      console.log('Merchant config found');
      return data;
    }

    if (rpcError) {
      console.error('Error fetching merchant config:', rpcError);
      throw rpcError;
    }

    const config = rpcData && rpcData.length > 0 ? rpcData[0] : null;
    if (!config) {
      console.warn('No merchant configuration found for:', merchantId);
      return null;
    }

    console.log('Merchant config found');
    return config;
  } catch (error) {
    console.error('Unexpected error in fetchMerchantConfig:', error);
    throw error;
  }
};

/**
 * Fetch location data including Google Maps URL
 */
export const fetchLocation = (locationId) =>
  withNetworkRetry(() => fetchLocationOnce(locationId));

const fetchLocationOnce = async (locationId) => {
  try {
    console.log('Fetching location:', locationId);

    const { data, error } = await supabase
      .from('locations')
      .select('id, location_name, google_maps_url')
      .eq('id', locationId)
      .single();

    if (error) {
      console.error('Error fetching location:', error);
      throw error;
    }

    console.log('Location found:', data?.location_name);
    return data;
  } catch (error) {
    console.error('Unexpected error in fetchLocation:', error);
    throw error;
  }
};

// ============================================================================
// RETURN SUBMISSION OPERATIONS
// ============================================================================

/**
 * Check if a return already exists for a booking.
 *
 * Wrapped in withNetworkRetry: this is the first call in the submit path and
 * is read-only, so retrying a transient fetch-layer failure (e.g. iOS
 * "Load failed" after the native camera tore down the connection) is safe.
 */
export const checkExistingReturn = (bookingId) =>
  withNetworkRetry(() => checkExistingReturnOnce(bookingId));

const checkExistingReturnOnce = async (bookingId) => {
  try {
    console.log('Checking for existing return:', bookingId);

    // Use RPC function to bypass RLS (app uses anon key)
    const { data: rpcData, error: rpcError } = await supabase
      .rpc('check_existing_booking_return', { p_booking_id: bookingId });

    // If RPC function doesn't exist, fall back to direct query
    if (rpcError && rpcError.code === 'PGRST202') {
      console.log('RPC function not found, falling back to direct query');
      const { data, error } = await supabase
        .from('booking_returns')
        .select('*')
        .eq('booking_id', bookingId)
        .eq('is_latest', true)
        .maybeSingle();

      if (error) {
        console.error('Error checking existing return:', error);
        throw error;
      }

      if (data) {
        console.log('Existing return found:', data.id);
      } else {
        console.log('No existing return found');
      }

      return data;
    }

    if (rpcError) {
      console.error('Error checking existing return:', rpcError);
      throw rpcError;
    }

    const existing = rpcData && rpcData.length > 0 ? rpcData[0] : null;

    if (existing) {
      console.log('Existing return found:', existing.id);
    } else {
      console.log('No existing return found');
    }

    return existing;
  } catch (error) {
    console.error('Unexpected error in checkExistingReturn:', error);
    throw error;
  }
};

/**
 * Create a new booking return.
 *
 * Wrapped in withNetworkRetry to survive transient fetch-layer failures during
 * submission. Retry is guarded against duplicates by checkExistingReturn, which
 * runs first in submitCompleteReturn, plus the per-submission submission_id.
 */
export const createBookingReturn = (returnData) =>
  withNetworkRetry(() => createBookingReturnOnce(returnData));

const createBookingReturnOnce = async (returnData) => {
  try {
    console.log('Creating booking return:', {
      booking_id: returnData.booking_id,
      submission_id: returnData.submission_id
    });

    const createdAtVienna = new Date().toLocaleString('en-CA', {
      timeZone: 'Europe/Vienna',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).replace(/,/g, '');

    // Use RPC function to bypass RLS (app uses anon key)
    const { data: rpcData, error: rpcError } = await supabase
      .rpc('create_booking_return', {
        p_booking_id: returnData.booking_id,
        p_merchant_id: returnData.merchant_id,
        p_location_id: returnData.location_id,
        p_completed_sections: returnData.completed_sections || [],
        p_booking_level_answers: returnData.booking_level_answers || {},
        p_overall_status: returnData.overall_status,
        p_description: returnData.description || '',
        p_submission_id: returnData.submission_id,
        p_created_at_vienna: createdAtVienna
      });

    // If RPC function doesn't exist, fall back to direct query
    if (rpcError && rpcError.code === 'PGRST202') {
      console.log('RPC function not found, falling back to direct insert');
      const { data, error } = await supabase
        .from('booking_returns')
        .insert([{
          booking_id: returnData.booking_id,
          merchant_id: returnData.merchant_id,
          location_id: returnData.location_id,
          completed_sections: returnData.completed_sections || [],
          booking_level_answers: returnData.booking_level_answers || {},
          overall_status: returnData.overall_status,
          description: returnData.description,
          submission_id: returnData.submission_id,
          is_latest: true,
          created_at_vienna: createdAtVienna
        }])
        .select()
        .single();

      if (error) {
        console.error('Error creating booking return:', error);
        throw error;
      }

      console.log('Booking return created successfully:', data.id);
      return data;
    }

    if (rpcError) {
      console.error('Error creating booking return:', rpcError);
      throw rpcError;
    }

    // RPC returns an array, get the first result
    const bookingReturn = rpcData && rpcData.length > 0 ? rpcData[0] : null;

    if (!bookingReturn) {
      throw new Error('No data returned from create_booking_return');
    }

    console.log('Booking return created successfully:', bookingReturn.id);
    return bookingReturn;
  } catch (error) {
    console.error('Unexpected error in createBookingReturn:', error);
    throw error;
  }
};

/**
 * Create booking item returns (item-level tracking).
 *
 * Wrapped in withNetworkRetry to survive transient fetch-layer failures during
 * submission.
 */
export const createBookingItemReturns = (itemReturns) =>
  withNetworkRetry(() => createBookingItemReturnsOnce(itemReturns));

const createBookingItemReturnsOnce = async (itemReturns) => {
  try {
    if (!itemReturns || itemReturns.length === 0) {
      console.log('No item returns to create');
      return [];
    }

    console.log('Creating booking item returns:', itemReturns.length);

    // Use RPC function to bypass RLS (app uses anon key)
    const { data: rpcData, error: rpcError } = await supabase
      .rpc('create_booking_item_returns', { p_items: itemReturns });

    // If RPC function doesn't exist, fall back to direct insert
    if (rpcError && rpcError.code === 'PGRST202') {
      console.log('RPC function not found, falling back to direct insert');
      const { data, error } = await supabase
        .from('booking_item_returns')
        .insert(itemReturns)
        .select();

      if (error) {
        console.error('Error creating booking item returns:', error);
        throw error;
      }

      console.log('Booking item returns created successfully:', data.length);
      return data;
    }

    if (rpcError) {
      console.error('Error creating booking item returns:', rpcError);
      throw rpcError;
    }

    console.log('Booking item returns created successfully:', rpcData?.length || 0);
    return rpcData || [];
  } catch (error) {
    console.error('Unexpected error in createBookingItemReturns:', error);
    throw error;
  }
};

/**
 * Upload photo to storage. Wrapped in withNetworkRetry; uses upsert:true so a
 * retry of the same path is idempotent (same content overwrites itself).
 */
export const uploadPhoto = (file, merchantId, bookingId, fileName) =>
  withNetworkRetry(() => uploadPhotoOnce(file, merchantId, bookingId, fileName));

const uploadPhotoOnce = async (file, merchantId, bookingId, fileName) => {
  try {
    const filePath = `${merchantId}/${bookingId}/${fileName}`;

    console.log('Uploading photo:', filePath);

    const { data, error } = await supabase.storage
      .from('return-pictures')
      .upload(filePath, file, {
        contentType: 'image/jpeg',
        upsert: true
      });

    if (error) {
      console.error('Error uploading photo:', error);
      throw error;
    }

    console.log('Photo uploaded successfully:', filePath);
    return { data, filePath };
  } catch (error) {
    console.error('Unexpected error in uploadPhoto:', error);
    throw error;
  }
};

/**
 * Create photo record in database.
 *
 * Wrapped in withNetworkRetry to survive transient fetch-layer failures during
 * submission (the storage upload in uploadPhoto is already retried + idempotent
 * via upsert).
 */
export const createPhotoRecord = (photoData) =>
  withNetworkRetry(() => createPhotoRecordOnce(photoData));

const createPhotoRecordOnce = async (photoData) => {
  try {
    console.log('Creating photo record:', photoData.file_path);

    const rpcParams = {
      p_booking_return_id: photoData.booking_return_id,
      p_booking_id: photoData.booking_id,
      p_merchant_id: photoData.merchant_id,
      p_location_id: photoData.location_id,
      p_booking_item_return_id: photoData.booking_item_return_id || null,
      p_file_name: photoData.file_name,
      p_file_path: photoData.file_path
    };

    // Use RPC function to bypass RLS (app uses anon key)
    let { data: rpcData, error: rpcError } = await supabase
      .rpc('create_booking_return_photo', photoData.section_id
        ? { ...rpcParams, p_section_id: photoData.section_id }
        : rpcParams);

    // PGRST202 with a section_id can mean the pre-006 seven-arg function is
    // still deployed — retry without the tag rather than losing the photo.
    if (rpcError && rpcError.code === 'PGRST202' && photoData.section_id) {
      console.log('RPC with p_section_id not found, retrying without section tag');
      ({ data: rpcData, error: rpcError } = await supabase
        .rpc('create_booking_return_photo', rpcParams));
    }

    // If RPC function doesn't exist, fall back to direct insert
    if (rpcError && rpcError.code === 'PGRST202') {
      console.log('RPC function not found, falling back to direct insert');
      const insertRow = {
        booking_return_id: photoData.booking_return_id,
        booking_id: photoData.booking_id,
        merchant_id: photoData.merchant_id,
        location_id: photoData.location_id,
        booking_item_return_id: photoData.booking_item_return_id || null,
        file_name: photoData.file_name,
        file_path: photoData.file_path
      };

      let { data, error } = await supabase
        .from('booking_return_photos')
        .insert([photoData.section_id
          ? { ...insertRow, section_id: photoData.section_id }
          : insertRow])
        .select()
        .single();

      // PGRST204 / 42703: section_id column not migrated yet — retry untagged.
      if (error && photoData.section_id &&
          (error.code === 'PGRST204' || error.code === '42703')) {
        console.log('section_id column not found, retrying insert without it');
        ({ data, error } = await supabase
          .from('booking_return_photos')
          .insert([insertRow])
          .select()
          .single());
      }

      if (error) {
        console.error('Error creating photo record:', error);
        throw error;
      }

      console.log('Photo record created successfully');
      return data;
    }

    if (rpcError) {
      console.error('Error creating photo record:', rpcError);
      throw rpcError;
    }

    const photoRecord = rpcData && rpcData.length > 0 ? rpcData[0] : null;
    console.log('Photo record created successfully');
    return photoRecord;
  } catch (error) {
    console.error('Unexpected error in createPhotoRecord:', error);
    throw error;
  }
};

/**
 * Upload photo and create record (convenience function)
 */
export const uploadPhotoComplete = async (file, photoData, fileName) => {
  try {
    // Upload to storage
    const { filePath } = await uploadPhoto(
      file,
      photoData.merchant_id,
      photoData.booking_id,
      fileName
    );

    // Create database record
    const record = await createPhotoRecord({
      ...photoData,
      file_name: fileName,
      file_path: filePath
    });

    return record;
  } catch (error) {
    console.error('Unexpected error in uploadPhotoComplete:', error);
    throw error;
  }
};

// ============================================================================
// COMPLETE SUBMISSION FLOW
// ============================================================================

/**
 * Submit complete return (booking + items + photos)
 */
export const submitCompleteReturn = async ({
  booking,
  merchantConfig,
  completedSections,
  bookingLevelAnswers,
  itemReturns,
  bookingPhotos,
  itemPhotos
}) => {
  try {
    console.log('Starting complete return submission');

    // 1. Check for existing return
    const existing = await checkExistingReturn(booking.id);
    if (existing) {
      console.log('Existing return found, marking it as not latest and creating new version');

      // Mark the existing return as not latest (use RPC to bypass RLS)
      const { error: rpcUpdateError } = await supabase
        .rpc('mark_booking_return_not_latest', { p_return_id: existing.id });

      // If RPC function doesn't exist, fall back to direct update
      if (rpcUpdateError && rpcUpdateError.code === 'PGRST202') {
        console.log('RPC function not found, falling back to direct update');
        const { error: updateError } = await supabase
          .from('booking_returns')
          .update({ is_latest: false })
          .eq('id', existing.id);

        if (updateError) {
          console.error('Error updating existing return:', updateError);
          throw updateError;
        }
      } else if (rpcUpdateError) {
        console.error('Error updating existing return:', rpcUpdateError);
        throw rpcUpdateError;
      }

      console.log('Previous return marked as not latest, proceeding with new submission');
    }

    // 2. Generate submission ID
    const submissionId = `${booking.order_id}_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;

    // 3. Determine overall status
    console.log('Determining overall status - itemReturns:', itemReturns);
    console.log('Item statuses:', itemReturns?.map(item => ({ id: item.booking_item_id, status: item.item_status })));

    const hasItemIssues = itemReturns?.some(item => item.item_status !== 'ok');
    const hasBookingIssues = Object.values(bookingLevelAnswers).some(value =>
      typeof value === 'string' && (
        value.includes('problem') ||
        value.includes('issue') ||
        value.includes('damage')
      )
    );

    console.log('Has item issues?', hasItemIssues);
    console.log('Has booking issues?', hasBookingIssues);

    const hasIssues = hasItemIssues || hasBookingIssues;
    const overallStatus = hasIssues ? 'issues_reported' : 'all_ok';

    console.log('Final overall status:', overallStatus);

    // 4. Create booking return
    const bookingReturn = await createBookingReturn({
      booking_id: booking.id,
      merchant_id: booking.merchant_id,
      location_id: booking.location_id,
      completed_sections: completedSections,
      booking_level_answers: bookingLevelAnswers,
      overall_status: overallStatus,
      description: '', // Will be generated from answers
      submission_id: submissionId
    });

    // 5. Create item returns (if any)
    let createdItemReturns = [];
    if (itemReturns && itemReturns.length > 0) {
      console.log('Creating item returns, count:', itemReturns.length);
      const itemReturnsWithReturnId = itemReturns.map(item => ({
        ...item,
        booking_return_id: bookingReturn.id,
        booking_id: booking.id
      }));
      console.log('Item returns with booking_return_id and booking_id:', itemReturnsWithReturnId);
      createdItemReturns = await createBookingItemReturns(itemReturnsWithReturnId);
      console.log('Created item returns:', createdItemReturns.length);
    } else {
      console.log('No item returns to create - itemReturns:', itemReturns);
    }

    // 6+7. Upload photos. Each photo is in its own try/catch so a single
    // failure no longer aborts the whole submission. Failures are collected
    // and surfaced as one aggregated error at the end.
    const failedPhotoErrors = [];
    let totalPhotoCount = 0;

    if (bookingPhotos && bookingPhotos.length > 0) {
      for (let i = 0; i < bookingPhotos.length; i++) {
        totalPhotoCount++;
        // Entries are { file, sectionId } (tagged per photo section); accept a
        // bare File too so older callers keep working.
        const entry = bookingPhotos[i];
        const photo = entry?.file ?? entry;
        const sectionId = entry?.sectionId ?? null;
        const fileName = `${booking.order_id}_${Date.now()}_${i}.jpg`;

        try {
          await uploadPhotoComplete(photo, {
            booking_return_id: bookingReturn.id,
            booking_id: booking.id,
            merchant_id: booking.merchant_id,
            location_id: booking.location_id,
            booking_item_return_id: null,
            section_id: sectionId
          }, fileName);
        } catch (err) {
          console.error('Failed to upload booking photo', i, err);
          failedPhotoErrors.push(err);
        }
      }
    }

    if (itemPhotos && Object.keys(itemPhotos).length > 0) {
      for (const [itemReturnId, photos] of Object.entries(itemPhotos)) {
        // itemPhotos is keyed by booking_item_id (set in ItemLevelFlow), so
        // match on booking_item_id. Also accept the row's own id to stay safe
        // against either shape returned by create_booking_item_returns.
        const itemReturn = createdItemReturns.find(
          ir => ir.booking_item_id === itemReturnId || ir.id === itemReturnId
        );
        if (!itemReturn) continue;

        for (let i = 0; i < photos.length; i++) {
          totalPhotoCount++;
          const photo = photos[i];
          const fileName = `${booking.order_id}_item_${itemReturn.inventory_item_id || itemReturn.booking_item_id}_${Date.now()}_${i}.jpg`;

          try {
            await uploadPhotoComplete(photo, {
              booking_return_id: bookingReturn.id,
              booking_id: booking.id,
              merchant_id: booking.merchant_id,
              location_id: booking.location_id,
              booking_item_return_id: itemReturn.id
            }, fileName);
          } catch (err) {
            console.error('Failed to upload item photo', itemReturnId, i, err);
            failedPhotoErrors.push(err);
          }
        }
      }
    }

    if (failedPhotoErrors.length > 0) {
      const aggregate = new Error(
        `Failed to upload ${failedPhotoErrors.length} of ${totalPhotoCount} photo(s)`
      );
      aggregate.partial = true;
      aggregate.failedPhotoCount = failedPhotoErrors.length;
      aggregate.totalPhotoCount = totalPhotoCount;
      aggregate.network = failedPhotoErrors.some(e => isTransientNetworkError(e));
      aggregate.firstCause = failedPhotoErrors[0];
      throw aggregate;
    }

    console.log('Complete return submission successful');

    return {
      status: 'success',
      bookingReturn,
      itemReturns: createdItemReturns
    };

  } catch (error) {
    console.error('Error in submitCompleteReturn:', error);
    throw error;
  }
};

// ============================================================================
// WEBHOOK (OPTIONAL - IF NEEDED)
// ============================================================================

/**
 * Call webhook for notification (optional)
 */
export const callWebhook = async (orderId, storeId, status) => {
  try {
    console.log('Calling webhook for:', { orderId, storeId, status });

    const response = await fetch('https://cloud.activepieces.com/api/v1/webhooks/ZQWjCysxyz4YAUgjMQSeb', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        order_id: orderId,
        store_id: storeId,
        status: status || 'completed'
      })
    });

    if (!response.ok) {
      throw new Error(`Webhook call failed: ${response.status} ${response.statusText}`);
    }

    const result = await response.json();
    console.log('Webhook called successfully:', result);
    return result;
  } catch (error) {
    console.error('Error calling webhook:', error);
    // Don't throw - webhook failure shouldn't block return submission
    return null;
  }
};

export default {
  supabase,
  resolveBookingToken,
  fetchBookingById,
  fetchBookingItems,
  fetchInventoryItemsForBooking,
  fetchMerchant,
  fetchMerchantConfig,
  fetchLocation,
  checkExistingReturn,
  createBookingReturn,
  createBookingItemReturns,
  uploadPhoto,
  createPhotoRecord,
  uploadPhotoComplete,
  submitCompleteReturn,
  callWebhook
};
